import {ApiMessageDataGroup, DataGroupModel} from '../../../models/api/dataGroupModel';
import {DraftMessageModel, MessageModel, MessageVisualStatusesEnum} from '../../../models/api/messageModel';
import {fetchApiAsync, postApiAsync} from '../../../services/http/http';
import {HttpResponseError} from '../../../services/http/httpError';
import {ResourceMap} from '../../appTypes';

/*
 * Constants.
 */

/** Disables conflict detection when saving or sending a message draft. */
export const unsafeIgnoreMessageVersionOptions: MessageVersionOptions = Object.freeze({
  getVersion: () => undefined,
});

/*
 * Types.
 */

export interface MessageVersionOptions {
  getVersion: () => string | undefined;
  setVersion?: (newVersion: string | undefined) => void;
}

/*
 * Helpers.
 */

export function createMessageVersionOptions(initialVersion: string | undefined): MessageVersionOptions {
  let version = initialVersion;

  return {
    getVersion: () => version,
    setVersion: (newVersion) => {
      version = newVersion;
    },
  };
}

export function maybeAddMessageVersionSetterToOptions<T extends MessageVersionOptions>(options: T): T {
  if (options.setVersion) {
    return options;
  }

  return {
    ...options,
    ...createMessageVersionOptions(options.getVersion()),
  };
}

export async function sendMessageApiAsync(message: DraftMessageModel, body: any): Promise<DataGroupModel> {
  const url = `${message.conversationUrl}/timeline?include_conversation=true`;
  return catchMessageAlreadySentAsync(message, () => postApiAsync(ApiMessageDataGroup, url, body));
}

export async function catchMessageAlreadySentAsync(
  message: DraftMessageModel,
  callbackAsync: () => Promise<DataGroupModel>,
): Promise<DataGroupModel> {
  try {
    return await callbackAsync();
  } catch (error) {
    // If the message is already sent, refresh our client-side state.
    // TODO: Make the API send the message in the error response.
    if (isMessageAlreadySentResponseError(error)) {
      return await fetchApiAsync(ApiMessageDataGroup, message.messageUrl);
    }

    throw error;
  }
}

function isMessageAlreadySentResponseError(error: unknown): boolean {
  return (
    error instanceof HttpResponseError && error.status === 403 && error.reason === 'Message already sent'
  );
}

export function findMessageById(
  messageId: number,
  messages: ResourceMap<MessageModel>,
): MessageModel | undefined {
  return Object.values(messages).find((m) => m?.id === messageId);
}

/**
 * @see replyToMessage from src/store/data/messages/draftMessagesActions.ts
 * @param originalMessage The original message that would be replied to e.g. the message with the ID
 * corresponding to the DraftModel.inReplyToId property.
 * @returns Whether or not the draft message action replyToMessage will allow replying to the original message.
 */
export function canReplyToMessage(originalMessage: MessageModel): boolean {
  return originalMessage.visualStatus !== MessageVisualStatusesEnum.PARTIAL;
}
